/**
 * Branding Cache Invalidation Hook
 * 
 * React hook that integrates branding cache management with the atomic reset system.
 * Provides reactive branding cache invalidation, community-specific clearing, and
 * automatic refresh capabilities.
 * 
 * Phase 3 implementation - reactive branding cache management
 */

import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { currentHostnameAtom } from '@/lib/state/brandingAtoms';
import { 
  invalidateBrandingCache,
  shouldRefreshBrandingCache,
  inspectBrandingCache,
  type BrandingInvalidationResult
} from '@/lib/services/brandingCacheIntegration';
import { isResetInProgress } from '@/lib/services/atomicReset';

// Development environment detection
const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Hook for managing branding cache invalidation
 */
export function useBrandingCacheInvalidation() {
  const queryClient = useQueryClient();
  const currentHostname = useAtomValue(currentHostnameAtom);
  const resetInProgress = isResetInProgress();

  /**
   * Invalidate branding cache for current hostname
   */
  const invalidateCurrentBranding = useCallback(async (
    reason?: string
  ): Promise<BrandingInvalidationResult> => {
    return invalidateBrandingCache(queryClient, currentHostname, reason);
  }, [queryClient, currentHostname]);

  /**
   * Invalidate all branding caches (global invalidation)
   */
  const invalidateAllBranding = useCallback(async (
    reason?: string
  ): Promise<BrandingInvalidationResult> => {
    return invalidateBrandingCache(queryClient, undefined, reason);
  }, [queryClient]);

  /**
   * Check if current branding cache should be refreshed
   */
  const shouldRefreshCurrent = useCallback((
    metaVersion: string
  ): boolean => {
    if (!currentHostname) return false;
    return shouldRefreshBrandingCache(currentHostname, metaVersion);
  }, [currentHostname]);

  /**
   * Refresh branding data if needed
   */
  const refreshBrandingIfNeeded = useCallback(async (
    metaVersion: string,
    reason?: string
  ): Promise<void> => {
    if (!currentHostname) return;

    if (shouldRefreshCurrent(metaVersion)) {
      if (isDevelopment) {
        console.log('BrandingCacheHook: Refreshing branding cache', { currentHostname, metaVersion, reason });
      }

      // Invalidate current branding to trigger refetch
      await invalidateCurrentBranding(`Meta-version refresh: ${reason || 'Unknown'}`);
    }
  }, [currentHostname, shouldRefreshCurrent, invalidateCurrentBranding]);

  /**
   * Development utility to inspect current cache state
   */
  const inspectCurrentCache = useCallback((): void => {
    if (isDevelopment) {
      console.log('BrandingCacheHook: Current hostname:', currentHostname);
      inspectBrandingCache();
    }
  }, [currentHostname]);

  // Log reset state changes for debugging
  useEffect(() => {
    if (isDevelopment && resetInProgress) {
      console.log('BrandingCacheHook: Reset in progress, branding operations suspended');
    }
  }, [resetInProgress]);

  return {
    // Current state
    currentHostname,
    resetInProgress,
    
    // Invalidation functions
    invalidateCurrentBranding,
    invalidateAllBranding,
    
    // Refresh utilities
    shouldRefreshCurrent,
    refreshBrandingIfNeeded,
    
    // Development utilities
    inspectCurrentCache: isDevelopment ? inspectCurrentCache : undefined,
  };
}

/**
 * Global branding cache listener hook
 * 
 * Listens for atomic reset events and automatically invalidates branding cache
 * when database changes are detected. Should be used at the app root level.
 */
export function useBrandingCacheResetListener() {
  const { invalidateAllBranding } = useBrandingCacheInvalidation();

  useEffect(() => {
    // Listen for custom events that might be dispatched during atomic resets
    const handleCacheReset = async (event: CustomEvent) => {
      if (isDevelopment) {
        console.log('BrandingCacheResetListener: Received cache reset event', event.detail);
      }

      try {
        const result = await invalidateAllBranding('Atomic reset triggered');
        if (isDevelopment) {
          console.log('BrandingCacheResetListener: Cache invalidation result', result);
        }
      } catch (error) {
        console.error('BrandingCacheResetListener: Failed to invalidate cache:', error);
      }
    };

    // Listen for custom cache reset events
    window.addEventListener('terrarium-cache-reset', handleCacheReset as EventListener);

    return () => {
      window.removeEventListener('terrarium-cache-reset', handleCacheReset as EventListener);
    };
  }, [invalidateAllBranding]);

  // Also provide direct invalidation capability
  return {
    triggerBrandingInvalidation: invalidateAllBranding
  };
}

/**
 * Community-aware branding cache hook
 * 
 * Provides branding cache management with community context awareness.
 * Automatically handles community switches and cross-community invalidation.
 */
export function useCommunityBrandingCache(communityId?: string) {
  const { 
    currentHostname, 
    invalidateCurrentBranding, 
    refreshBrandingIfNeeded 
  } = useBrandingCacheInvalidation();

  /**
   * Invalidate branding when switching communities
   */
  const handleCommunitySwitch = useCallback(async (
    newCommunityId: string,
    previousCommunityId?: string
  ): Promise<void> => {
    if (previousCommunityId && previousCommunityId !== newCommunityId) {
      if (isDevelopment) {
        console.log('CommunityBrandingCache: Community switch detected', {
          from: previousCommunityId,
          to: newCommunityId
        });
      }

      // Invalidate current branding to ensure fresh data for new community
      await invalidateCurrentBranding(`Community switch: ${previousCommunityId} → ${newCommunityId}`);
    }
  }, [invalidateCurrentBranding]);

  /**
   * Refresh branding for current community
   */
  const refreshCommunityBranding = useCallback(async (
    metaVersion: string
  ): Promise<void> => {
    if (communityId) {
      await refreshBrandingIfNeeded(
        metaVersion, 
        `Community branding refresh for ${communityId}`
      );
    }
  }, [communityId, refreshBrandingIfNeeded]);

  return {
    currentHostname,
    communityId,
    handleCommunitySwitch,
    refreshCommunityBranding,
    invalidateCurrentBranding,
  };
}